// src/Payroll_Management/PayRoll_Report/EmployeeJoiningReportPrint.jsx
import { useEffect, useMemo, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { Printer, X } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE_URL = "https://nissi-india.com";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}
function s(v) {
  return (v ?? "").toString().trim();
}

const EmployeeJoiningReportPrint = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const navigate = useNavigate();
  const q = useQuery();

  const finYearId = localStorage.getItem("finYearId") || "";
  const empJoinId = q.get("empJoinId") || "";

  const [loading, setLoading] = useState(false);
  const [row, setRow] = useState(null);
  const [photoDataUrl, setPhotoDataUrl] = useState("");

  // Static header (same as screenshot)
  // If you want company dynamic later, we can fetch from tbl_company_mas.
  const company = {
    name: "Nissi Infotech",
    subtitle: "CIVIL CONSTRUCTION",
    address: "3/42-12, Nagappa colony, Kavundampalayam, Coimbatore - 641030, Tamil Nadu, India.",
    phone: "Phone No : 9003744644",
    email: "infraconsoft-implementations",
  };

  const btn =
    "inline-flex items-center gap-2 px-5 py-2 border font-bold text-sm shadow " +
    (isDark ? "bg-black text-white border-black" : "bg-black text-white border-black");

  const btnOutline =
    "inline-flex items-center gap-2 px-5 py-2 border font-bold text-sm shadow " +
    (isDark ? "bg-white text-black border-black" : "bg-white text-black border-black");

  const fetchPrint = async () => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!s(empJoinId)) {
      toast.warn("Missing empJoinId");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-joining-report/print-data`, {
        params: { finYearId: Number(finYearId), empJoinId },
        timeout: 60000,
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Print data load failed");
        return;
      }

      setRow(res.data.row || null);
      setPhotoDataUrl(res.data.photoDataUrl || "");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrint = () => window.print();
  const handleClose = () => {
    // close tab if opened by window.open; else navigate back
    if (window.opener) window.close();
    else navigate(-1);
  };

  // Helpers: safely read columns (DB column names may vary)
  const val = (key1, key2) => {
    if (!row) return "";
    if (key1 && row[key1] != null) return row[key1];
    if (key2 && row[key2] != null) return row[key2];
    return "";
  };

  const reportTitle = useMemo(() => {
    // try common fields, else default HEAD-OFFICE like screenshot
    const toBranch =
      val("ToBranchName", "fld_tobranchname") ||
      val("fld_BranchName", "tobranch") ||
      "HEAD-OFFICE";
    return `JOINING REPORT - ${toBranch}`;
  }, [row]); // eslint-disable-line

  return (
    <div className={`${isDark ? "bg-gray-900 text-white" : "bg-gray-200 text-black"} min-h-screen`}>
      <ToastContainer
        position="top-right"
        autoClose={2500}
        theme={isDark ? "dark" : "light"}
      />

      {/* Top buttons - hide in print */}
      <div className="flex justify-end gap-3 p-4 print:hidden">
        <button onClick={handlePrint} className={btn}>
          <Printer className="w-4 h-4" />
          Print
        </button>
        <button onClick={handleClose} className={btnOutline}>
          <X className="w-4 h-4" />
          Close
        </button>
      </div>

      {/* Page */}
      <div className="flex justify-center px-4 pb-10">
        <div
          className={`w-[900px] bg-white text-black shadow-2xl border border-gray-500 print:shadow-none print:border-0`}
          style={{ fontFamily: "Times New Roman, serif" }}
        >
          {/* outer padding */}
          <div className="p-4">
            {/* inner border */}
            <div className="border border-black p-3">
              {/* header row */}
              <div className="border border-black p-3">
                <div className="flex items-start gap-4">
                  {/* logo box */}
                  <div className="w-[110px] h-[90px] border border-black flex items-center justify-center text-[10px] font-bold">
                    LOGO
                  </div>

                  <div className="flex-1 text-center">
                    <div className="text-[22px] font-bold">{company.name}</div>
                    <div className="text-[13px] font-bold">{company.subtitle}</div>
                    <div className="text-[12px] mt-1">{company.address}</div>
                    <div className="text-[12px]">{company.phone}</div>
                    <div className="text-[12px]">{company.email}</div>
                  </div>
                </div>

                <div className="border-t border-black mt-3" />
                <div className="text-center font-bold text-[14px] py-2">
                  {reportTitle}
                </div>
              </div>

              {/* body */}
              <div className="mt-4 grid grid-cols-12 gap-3">
                {/* left details */}
                <div className="col-span-9">
                  <table className="w-full text-[13px]">
                    <tbody>
                      <tr>
                        <td className="w-[200px] font-bold py-1">Transfer Number</td>
                        <td className="w-[10px]">:</td>
                        <td className="py-1">{val("TransferNumber", "empcode") || val("fld_employee_transferemployeecode", "transfercode")}</td>
                      </tr>
                      <tr>
                        <td className="font-bold py-1">Employee Code</td>
                        <td>:</td>
                        <td className="py-1">{val("EmployeeCode", "fld_Employeecode") || val("code", "empcode")}</td>
                      </tr>
                      <tr>
                        <td className="font-bold py-1">Employee Name</td>
                        <td>:</td>
                        <td className="py-1">{val("EmployeeName", "fld_Employee") || val("empname", "employee")}</td>
                      </tr>
                      <tr>
                        <td className="font-bold py-1">Designation</td>
                        <td>:</td>
                        <td className="py-1">{val("Designation", "fld_designationname") || val("designme", "designation")}</td>
                      </tr>
                      <tr>
                        <td className="font-bold py-1">From Branch</td>
                        <td>:</td>
                        <td className="py-1">{val("FromBranch", "FromBranchName") || val("brnme", "fld_FromBranchName")}</td>
                      </tr>
                      <tr>
                        <td className="font-bold py-1">Transfer Effective Date</td>
                        <td>:</td>
                        <td className="py-1">{val("TransferEffectiveDate", "TransferDate") || val("effectivedate", "fld_employee_effectdate")}</td>
                      </tr>
                      <tr>
                        <td className="font-bold py-1">Join Date</td>
                        <td>:</td>
                        <td className="py-1">{val("JoinDate", "trdate") || val("joindate", "fld_joindate")}</td>
                      </tr>
                      <tr>
                        <td className="font-bold py-1">Monthly Salary</td>
                        <td>:</td>
                        <td className="py-1">{val("MonthlySalary", "monthlysalary") || val("fld_grosssalary", "salary")}</td>
                      </tr>
                      <tr>
                        <td className="font-bold py-1">Daily Batta</td>
                        <td>:</td>
                        <td className="py-1">{val("DailyBatta", "dailybatta") || val("fld_dailybata", "batta")}</td>
                      </tr>
                      <tr>
                        <td className="font-bold py-1">HRA</td>
                        <td>:</td>
                        <td className="py-1">{val("HRA", "hra") || val("fld_SpecialHRA", "hraamt")}</td>
                      </tr>
                      <tr>
                        <td className="font-bold py-1">Salary Advance</td>
                        <td>:</td>
                        <td className="py-1">{val("fld_balanceamount", "salaryadvance") || val("fld_salaryadvance", "saladv")}</td>
                      </tr>
                      <tr>
                        <td className="font-bold py-1">Batta Paid Upto</td>
                        <td>:</td>
                        <td className="py-1">{val("BattaPaidUpto", "battapaidupto") || val("fld_employee_batapaidupto", "battaupto")}</td>
                      </tr>
                      <tr>
                        <td className="font-bold py-1">Travelling Advance</td>
                        <td>:</td>
                        <td className="py-1">{val("TravellingAdvance", "travellingadvance") || val("fld_traveladvance", "tadv")}</td>
                      </tr>
                      <tr>
                        <td className="font-bold py-1">Canteen Due</td>
                        <td>:</td>
                        <td className="py-1">{val("CanteenDue", "canteendue") || val("fld_employee_canteendue", "canteen")}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* right photo */}
                <div className="col-span-3 flex justify-end">
                  <div className="w-[140px] h-[160px] border border-blue-600 flex items-center justify-center overflow-hidden">
                    {photoDataUrl ? (
                      <img
                        src={photoDataUrl}
                        alt="Employee"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="text-orange-600 text-[12px] font-bold text-center">
                        No Image <br /> Help!
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* footer signatures */}
              <div className="mt-10 flex justify-between text-[13px]">
                <div className="font-bold">Prepared By</div>
                <div className="text-right">
                  <div className="font-bold">Authorized Signatory</div>
                  <div className="text-[11px] mt-2">
                    {val("fld_employee_joinby", "preparedby") || ""}
                  </div>
                  <div className="text-[11px]">
                    {val("fld_created_on", "printdatetime") || ""}
                  </div>
                </div>
              </div>

              {loading && (
                <div className="mt-4 text-center text-sm font-bold">
                  Loading...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Print CSS */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default EmployeeJoiningReportPrint;
